import React from "react";
import PropTypes from "prop-types";
import CategoryFilter from "./CategoryFilter";
import NonCategoryFilter from "./NonCategoryFilter";
import PriceFilter from "./PriceFilter";
import BrandFilter from "./BrandFilter";
import MobileFilterContainer from "./MobileFilterContainer";
import style from "../../stylesheets/StoreFilterContainer.module.scss";
import utility from "../../services/utility";

import {
  CATEGORY_FACET_ID,
  COLOR_FACET_ID,
  PRICE_PSEUDO_FACET_ID,
} from "../../constants";

const StoreFilterContainer = ({
  facetFilters,
  selectedDropdown,
  facetParamsName,
  showDropdown,
  nbHits,
  isMobile,
  toggleMobileFilter,
  mobileFilterOpened,
  mobileHandleFilterClick,
  mobileFacetParams,
  min_price_input,
  max_price_input,
  maxPrice,
  minPrice,
  clearAllFilters,
  handlePriceFilterClick,
  handlePriceFilterInput,
  categoryTargetFilter,
  facetParams,
  isUltimateParentCategory,
  showingLowestCategoryLevel,
  categoryListOfFilters,
  categoryListWithCounts,
  parentFilterExists,
  handleRemoveCategoryFilter,
  handleCategoryFilterClick,
  slideCategoryFilters,
  loadFiltersAndResults,
  clearMultiSelectFilter,
  clearPriceFilter,
  expandFilter,
  handleFilterClick,
  brandParamsName,
}) => {
  // set the category filter as the first/last filter by swapping
  const reorderedFacetFilters = utility.reorderCategoryFilter(facetFilters);

  let fakeParentStoreExists = false;

  const facetFiltersElems = reorderedFacetFilters.map((facetFilter) => {
    let facetParamTagIds = [];

    if (facetParams[facetFilter.facet.id]) {
      if (typeof facetParams[facetFilter.facet.id] === "object") {
        facetParamTagIds = facetParams[facetFilter.facet.id];
      } else {
        facetParamTagIds = [facetParams[facetFilter.facet.id]];
      }
    }

    let mobileFacetParamTagIds = [];

    if (isMobile) {
      if (mobileFacetParams[facetFilter.facet.id]) {
        if (typeof mobileFacetParams[facetFilter.facet.id] === "object") {
          mobileFacetParamTagIds = mobileFacetParams[facetFilter.facet.id];
        } else {
          mobileFacetParamTagIds = [mobileFacetParams[facetFilter.facet.id]];
        }
      }
    }

    const correspondingFacetParamTagIds = isMobile
      ? mobileFacetParamTagIds
      : facetParamTagIds;

    const storeFilterProps = {
      key: facetFilter.facet.id,
      facetFilter,
      selectedDropdown,
      showingLowestCategoryLevel,
      showDropdown,
      categoryTargetFilter,
      facetParamTagIds,
      mobileFacetParamTagIds,
      correspondingFacetParamTagIds,
      categoryListOfFilters,
      categoryListWithCounts,
      parentFilterExists,
      handleCategoryFilterClick,
      handleFilterClick,
      slideCategoryFilters,
      loadFiltersAndResults,
      isMobile,
      mobileHandleFilterClick,
      clearMultiSelectFilter,
      brandParamsName,
    };

    if (facetFilter.facet.id === CATEGORY_FACET_ID) {
      fakeParentStoreExists =
        Object.keys(categoryTargetFilter).length > 0 &&
        !parentFilterExists &&
        ((correspondingFacetParamTagIds.length === 0 &&
          Object.keys(facetFilter.target_facet_tag).length === 0) ||
          (correspondingFacetParamTagIds.length > 0 &&
            Object.keys(facetFilter.target_facet_tag).length > 0));
      storeFilterProps.fakeParentStoreExists = fakeParentStoreExists;
      return <CategoryFilter {...storeFilterProps} />;
    }

    return <NonCategoryFilter {...storeFilterProps} />;
  });

  const priceFilter = (
    <PriceFilter
      maxPrice={maxPrice}
      minPrice={minPrice}
      min_price_input={min_price_input}
      max_price_input={max_price_input}
      selectedDropdown={selectedDropdown}
      showDropdown={showDropdown}
      handlePriceFilterClick={handlePriceFilterClick}
      handlePriceFilterInput={handlePriceFilterInput}
      clearPriceFilter={clearPriceFilter}
      isMobile={isMobile}
    />
  );

  const brandFilter = (
    <BrandFilter
      selectedDropdown={selectedDropdown}
      showDropdown={showDropdown}
      isMobile={isMobile}
      handleFilterClick={handleFilterClick}
      brandParamsName={brandParamsName}
    />
  );

  const handleRemoveFilter = (selectedFilter) => {
    if (selectedFilter.facet_id === PRICE_PSEUDO_FACET_ID) {
      clearPriceFilter();
    } else if (selectedFilter.facet_id === CATEGORY_FACET_ID) {
      selectedFilter.fakeParentStoreExists = fakeParentStoreExists;
      handleRemoveCategoryFilter(selectedFilter);
    } else {
      handleFilterClick({ filter: selectedFilter, removeFilter: true });
    }
  };

  const selectedFilters = facetParamsName.map((selectedFilter) => {
    if (selectedFilter) {
      let selectedFilterContent = null;

    if (selectedFilter.facet_id === COLOR_FACET_ID) {
      if (
        selectedFilter.metadata &&
        selectedFilter.metadata.hex !== undefined
      ) {
        if (selectedFilter.metadata.hex.length === 1) {
          const backgroundColor = `#${selectedFilter.metadata.hex[0]}`;
          const colorHexCode = { backgroundColor };

            selectedFilterContent = (
              <div className={style.selectedColorFilter} style={colorHexCode} />
            );
          }

          if (selectedFilter.metadata.hex.length === 2) {
            selectedFilterContent = (
              <div className={style.selectedColorFilter}>
                <div
                  className={style.colorHexCodeBefore}
                  style={{
                    backgroundColor: `#${selectedFilter.metadata.hex[0]}`,
                  }}
                />
                <div />
                <div
                  className={style.colorHexCodeAfter}
                  style={{
                    backgroundColor: `#${selectedFilter.metadata.hex[1]}`,
                  }}
                />
              </div>
            );
          }
        }
      } else if (
        selectedFilter.facet_id === CATEGORY_FACET_ID &&
        isUltimateParentCategory &&
        !fakeParentStoreExists
      ) {
        // do not show the ultimate parent category as a selected filter
        return null;
      } else if (selectedFilter.facet_id === PRICE_PSEUDO_FACET_ID) {
        selectedFilterContent = <span>{`${selectedFilter.name}`}</span>;
      } else {
        selectedFilterContent = (
          <span>
            {`${selectedFilter.facet_name || null} - ${selectedFilter.name}`}
          </span>
        );
      }

      return (
        <div
          className={`selectedFilter ${style.selectedFilter}`}
          onClick={() => handleRemoveFilter(selectedFilter)}
          key={selectedFilter.id}
          tabIndex="0"
        >
          <img
            className={style.deleteFilter}
            src="https://cdn-s3.touchofmodern.com/images/icons/icon_filterDeleteFilters%402x.png"
            alt="filterDelete"
          />
          {selectedFilterContent}
        </div>
      );
    }
  });

  const handleClearAll = () => {
    clearAllFilters();
  };

  const clearAllButton =
    facetParamsName.length === 0 ||
    (facetParamsName.length === 1 &&
      facetParamsName[0] !== undefined &&
      facetParamsName[0].facet_id === CATEGORY_FACET_ID &&
      isUltimateParentCategory &&
      !fakeParentStoreExists) ? null : (
      <div
        className={`clearAllButton ${style.clearAllFilter}`}
        onClick={handleClearAll}
        tabIndex="0"
      >
        Clear all
      </div>
    );

  const emptyBottomContainer =
    selectedFilters.length === 0 ? style.emptyBottomContainer : "";

  return isMobile ? (
    <MobileFilterContainer
      nbHits={nbHits}
      toggleMobileFilter={toggleMobileFilter}
      mobileFilterOpened={mobileFilterOpened}
      facetFiltersElems={facetFiltersElems}
      priceFilter={priceFilter}
      brandFilter={brandFilter}
      loadFiltersAndResults={loadFiltersAndResults}
    />
  ) : (
    <div>
      <div className={`${style.topContainer}`}>
        <div className={style.selectedFiltersContainer}>
          <div
            className={`filtersContainerTitle ${style.filtersContainerTitle}`}
            onClick={expandFilter}
            tabindex="0"
            role="heading"
            aria-level="2"
          >
            <div className={`filtersTitle ${style.filtersTitle}`} />
            Filters
            <span className={"selectedFiltersNumber"}>
              &nbsp;({selectedFilters.length})
            </span>
          </div>
          <div className={`selectedFilters ${style.selectedFilters}`}>
            {selectedFilters}
            {clearAllButton}
          </div>
        </div>
        <div className={style.resultsNumber}>{nbHits} results</div>
      </div>
      <div
        className={`filtersContainer ${style.bottomContainer} ${emptyBottomContainer}`}
      >
        {facetFiltersElems}
        {brandFilter}
        {priceFilter}
      </div>
    </div>
  );
};

StoreFilterContainer.propTypes = {
  facetFilters: PropTypes.array.isRequired,
  selectedDropdown: PropTypes.number.isRequired,
  nbHits: PropTypes.number.isRequired,
  facetParamsName: PropTypes.array.isRequired,
  showDropdown: PropTypes.func.isRequired,
  maxPrice: PropTypes.number.isRequired,
  minPrice: PropTypes.number.isRequired,
  min_price_input: PropTypes.number.isRequired,
  max_price_input: PropTypes.number.isRequired,
  clearAllFilters: PropTypes.func.isRequired,
  handlePriceFilterClick: PropTypes.func.isRequired,
  handlePriceFilterInput: PropTypes.func.isRequired,
  facetParams: PropTypes.object.isRequired,
  categoryTargetFilter: PropTypes.object.isRequired,
  categoryListOfFilters: PropTypes.array.isRequired,
  categoryListWithCounts: PropTypes.array.isRequired,
  parentFilterExists: PropTypes.bool.isRequired,
  isUltimateParentCategory: PropTypes.bool.isRequired,
  showingLowestCategoryLevel: PropTypes.bool.isRequired,
  handleRemoveCategoryFilter: PropTypes.func.isRequired,
  handleCategoryFilterClick: PropTypes.func.isRequired,
  clearPriceFilter: PropTypes.func.isRequired,
  clearMultiSelectFilter: PropTypes.func.isRequired,
  handleFilterClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  toggleMobileFilter: PropTypes.func.isRequired,
  mobileFilterOpened: PropTypes.bool.isRequired,
  mobileHandleFilterClick: PropTypes.func.isRequired,
  mobileFacetParams: PropTypes.object.isRequired,
  slideCategoryFilters: PropTypes.func.isRequired,
  loadFiltersAndResults: PropTypes.func.isRequired,
};

export default StoreFilterContainer;
